import { Checkpoint } from '../api'
import { useTheme } from '../contexts/ThemeContext'
import { Moon, Sun, RotateCw, Play, Plus, Search } from 'lucide-react'

interface ResumeSessionScreenProps {
  checkpoint: Checkpoint
  onContinue: () => void
  onNewSession: () => void
  isLoading: boolean
  onManualSearch: () => void
}

export function ResumeSessionScreen({
  checkpoint,
  onContinue,
  onNewSession,
  isLoading,
  onManualSearch
}: ResumeSessionScreenProps) {
  const { theme, toggleTheme, logo } = useTheme()
  const lastUpdate = new Date(checkpoint.lastUpdatedAt).toLocaleString('pt-BR')
  const progress = Math.round((checkpoint.currentIndex / checkpoint.totalProducts) * 100)

  return (
    <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark transition-colors duration-300 flex items-center justify-center p-3 sm:p-4">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 sm:top-6 right-4 sm:right-6 p-2 rounded-lg bg-gray-100 dark:bg-[#232326] hover:bg-gray-200 dark:hover:bg-[#343437] transition-colors flex-shrink-0"
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className="bg-white dark:bg-[#232326] rounded-xl shadow-xl p-6 sm:p-8 max-w-md w-full border border-gray-200 dark:border-[#5A5A5E]">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src={logo}
            alt="Seridóplast"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </div>

        <div className="text-center mb-4 sm:mb-6">
          <RotateCw size={40} className="mx-auto mb-2 sm:mb-3 text-[#E5191F] dark:text-[#FCD016] sm:block hidden" />
          <RotateCw size={32} className="mx-auto mb-2 sm:mb-3 text-[#E5191F] dark:text-[#FCD016] sm:hidden" />
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] bg-clip-text text-transparent">Sessão Encontrada</h1>
        </div>

        <div className="bg-[#E5191F]/5 dark:bg-[#FCD016]/5 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border border-[#E5191F]/20 dark:border-[#FCD016]/20">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-3 sm:mb-4">
            <p className="mb-2 sm:mb-3">
              <span className="font-semibold">Você parou em:</span>
            </p>
            <p className="text-lg sm:text-xl text-[#E5191F] dark:text-[#FCD016] font-mono font-bold">
              Produto {checkpoint.currentIndex + 1} de {checkpoint.totalProducts}
            </p>
            {checkpoint.currentProductCode && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                Código: <span className="font-mono">{checkpoint.currentProductCode}</span>
              </p>
            )}
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
            <p>Última atualização:</p>
            <p className="truncate">{lastUpdate}</p>
          </div>

          <div className="mb-3 sm:mb-4">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
              Progresso: <span className="text-[#E5191F] dark:text-[#FCD016]">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-[#343437] rounded-full h-2">
              <div
                className="bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="text-xs text-gray-600 dark:text-gray-400 mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-3">
            <div className="text-center">
              <p className="font-bold text-base sm:text-lg text-[#E5191F] dark:text-[#FCD016]">{checkpoint.approved}</p>
              <p className="text-xs">Aprovados</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-base sm:text-lg text-orange-600 dark:text-orange-400">{checkpoint.skipped}</p>
              <p className="text-xs">Pulados</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-base sm:text-lg text-gray-600 dark:text-gray-400">{checkpoint.noImage}</p>
              <p className="text-xs">Sem imagem</p>
            </div>
          </div>
        </div>

        <div className="space-y-2 sm:space-y-3">
          <button
            onClick={onContinue}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-[#E5191F] to-[#F03B40] hover:from-[#C91419] hover:to-[#E01A36] dark:from-[#D9B20F] dark:to-[#FCD016] dark:hover:from-[#C9A00A] dark:hover:to-[#F0BA00] text-white dark:text-[#18181A] font-bold py-2 sm:py-3 rounded-lg transition disabled:opacity-50 transform hover:scale-105 flex items-center justify-center gap-2 text-xs sm:text-base"
          >
            <Play size={14} className="sm:block hidden flex-shrink-0" />
            <Play size={12} className="sm:hidden flex-shrink-0" />
            {isLoading ? 'Carregando...' : 'Continuar de Onde Parei'}
          </button>


          <button
            onClick={onManualSearch}
            disabled={isLoading}
            className="w-full bg-[#414142] hover:bg-[#2D2D30] text-white font-bold py-2 sm:py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2 text-xs sm:text-base"
          >
            <Search size={14} />
            Pesquisa Avulsa
          </button>

          <button
            onClick={onNewSession}
            disabled={isLoading}
            className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-[#343437] dark:hover:bg-[#414142] text-gray-900 dark:text-white font-bold py-2 sm:py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2 text-xs sm:text-base"
          >
            <Plus size={14} className="sm:block hidden flex-shrink-0" />
            <Plus size={12} className="sm:hidden flex-shrink-0" />
            Iniciar Nova Sessão
          </button>
        </div>
      </div>
    </div>
  )
}
